import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import useTranslation from "../../../../../../packages/locales/translations/util/useTranslation";
import { useParams } from "react-router";
import { useArticleById } from "@coworker/app/src/components/RoomSettings/hooks/useArticleById";
import { useMediasAndArticleCount } from "@coworker/app/src/components/RoomSettings/hooks/useMediasAndArticleCount";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import {
  ArticleListItemMobile,
  SimpleContainer,
} from "@coworker/app/src/components/RoomSettings/styles/styles";
import Trans from "../../../../../../packages/locales/translations/util/Trans";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { MediaPlacementSectionSearch } from "../../../components/MediaPlacementSection/MediaPlacementSectionSearch/MediaPlacementSectionSearch";
import { AboutArticleSection } from "../../../components/AboutArticleSection/AboutArticleSection";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import React from "react";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { useEDSData } from "@coworker/app/src/components/RoomSettings/hooks/useEDSData";
import { ItemListViewItemCard } from "../../../components/item-cards/ItemListViewItemCard/ItemListViewItemCard";
import { Article } from "@coworker/app/src/components/RoomSettings/types/article";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { ArticleViewSkeleton } from "@coworker/app/src/components/RoomSettings/Skeletons/ArticleViewSkeleton";
import { MediaItemType } from "../../../types/mediaItemType";
import { MediaItemViewType } from "../../../types/mediaItemViewType";

export const MediaArticleInformationPageSearch = () => {
  const navigate = useFixaNavigate();
  const { t } = useTranslation();
  const { productArticleId } = useParams();
  const storeId = useStoreId();

  const {
    data: mediasAndArticleCount,
    isFetching: isMediasAndArticleCountFetching,
  } = useMediasAndArticleCount(productArticleId, storeId);

  const { data: searchedArticle, isFetching: isArticleFetching } =
    useArticleById(mediasAndArticleCount?.[0]?.articleId);

  const countryId = useCountryId();
  const { data: edsData } = useEDSData(countryId, productArticleId || "");
  const { data: itemInfo, isLoading: isItemInfoLoading } = useItemInfo(
    productArticleId ?? ""
  );

  const totalQuantityInPieces = mediasAndArticleCount?.reduce(
    (acc, media) => acc + media.nbrArticles,
    0
  );

  const itemCardContent = React.useMemo(() => {
    if (!searchedArticle) return null;
    return (
      <ItemListViewItemCard<Article>
        edsData={edsData?.[0] || null}
        mediaItemType={MediaItemType.REGULAR_ITEM}
        showActions={false}
        itemCardRenderType={ItemCardRenderType.static}
        isItemInGroups={false}
        badges={[]}
        article={searchedArticle}
        mediaItemViewType={MediaItemViewType.SIMPLE_ITEM_VIEW}
        itemInfo={itemInfo ?? null}
        itemInfoLoading={isItemInfoLoading}
        shouldDisplayQuantity={true}
        totalQuantityPieces={totalQuantityInPieces || 0}
      />
    );
  }, [
    searchedArticle,
    edsData,
    itemInfo,
    isItemInfoLoading,
    totalQuantityInPieces,
  ]);

  const linkbar = React.useMemo(() => {
    return (
      <Linkbar
        items={[
          {
            label: t("mediaMaintenanceString"),
            to: "/mediamaintenance",
          },
        ]}
        currentLabel={searchedArticle?.itemName ?? ""}
        currentAsH1
      />
    );
  }, [searchedArticle?.itemName, t]);

  if (
    !isMediasAndArticleCountFetching &&
    !isArticleFetching &&
    !searchedArticle
  )
    return (
      <>
        {linkbar}
        <SimpleContainer
          $direction={"column"}
          onClick={() => {
            navigate("/mediamaintenance");
          }}
        >
          <Trans values={{ article: productArticleId }}>
            articleDoesNotExistInRoomSettingString
          </Trans>
          <FixaButton
            type={"primary"}
            text={t("goBackToRoomListingString")}
            onClick={() => {
              navigate("/mediamaintenance");
            }}
          />
        </SimpleContainer>
      </>
    );

  if (
    !searchedArticle &&
    (isMediasAndArticleCountFetching || isArticleFetching)
  )
    return (
      <FullScreenPopup noPadding linkbar={linkbar}>
        <ArticleViewSkeleton />
      </FullScreenPopup>
    );

  if (!searchedArticle) return null;

  return (
    <FullScreenPopup
      noPadding
      linkbar={linkbar}
      actionBarContent={
        <FixaButton type="primary" fluid text={t("createString")} />
      }
    >
      <FixaListView id="searched-article-list-view" size="small">
        <ArticleListItemMobile>{itemCardContent}</ArticleListItemMobile>
        {isMediasAndArticleCountFetching ? (
          <ArticleViewSkeleton />
        ) : (
          <MediaPlacementSectionSearch
            itemCardContent={itemCardContent}
            mediasAndArticleCount={mediasAndArticleCount || []}
          />
        )}
        <AboutArticleSection
          itemNoAndType={{
            no: searchedArticle.productArticleId ?? "",
            type: searchedArticle.productArticleType ?? "",
          }}
        />
      </FixaListView>
    </FullScreenPopup>
  );
};
